import { createFileRoute, Link } from "@tanstack/react-router";
import { Clock, MapPinned, PlaneLanding, ShieldCheck, Wallet } from "lucide-react";

import { CarGrid } from "@/components/site/CarGrid";
import { LocationMap } from "@/components/site/LocationMap";
import { Button } from "@/components/ui/button";
import { cars } from "@/data/cars";
import { getPickupPoint } from "@/data/locations";
import { hreflangLinks } from "@/lib/i18n";
import { publicPageHeaders } from "@/lib/http-cache";

const CANONICAL = "https://carentals.ro/car-rental-otopeni-airport";
const title = "Car Rental Otopeni Airport — Free Pickup, No Deposit";
const description =
  "Rent a car at Otopeni Airport (OTP) with free pickup and no deposit. No credit card needed, insurance and rovinieta included, 24/7 handover from €44/day.";

export const Route = createFileRoute("/car-rental-otopeni-airport")({
  headers: publicPageHeaders,
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      {
        name: "keywords",
        content:
          "rent a car otopeni, car rental otopeni airport, otp airport car hire, car rental bucharest airport no deposit",
      },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      ...hreflangLinks("/car-rental-otopeni-airport", "/ro/inchirieri-auto-otopeni"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Car rental at Otopeni Airport (OTP), Bucharest",
          serviceType: "Airport car rental",
          url: CANONICAL,
          provider: { "@id": "https://carentals.ro/#organization" },
          areaServed: {
            "@type": "Airport",
            name: "Henri Coandă International Airport",
            iataCode: "OTP",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Otopeni",
              addressCountry: "RO",
            },
          },
          offers: {
            "@type": "Offer",
            price: "44",
            priceCurrency: "EUR",
            availability: "https://schema.org/InStock",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              q: "Is airport pickup really free?",
              a: "Yes. We hand over and collect the car at Henri Coandă (Otopeni) Airport free of charge, at any hour, including night arrivals.",
            },
            {
              q: "Do I need a credit card or a deposit at the airport?",
              a: "No. There is no security deposit and no credit card requirement. A debit card, cash in EUR or RON, or a bank transfer is enough.",
            },
            {
              q: "Where exactly do we meet at OTP?",
              a: "Our driver waits in the Arrivals hall with a name sign and walks you to the car in the short-stay parking, a two-minute walk from the terminal.",
            },
            {
              q: "What if my flight is delayed?",
              a: "Send us your flight number when booking. We track the flight and adjust the pickup time at no extra cost.",
            },
          ].map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

const perks = [
  {
    icon: PlaneLanding,
    title: "Free meet & greet at OTP",
    text: "We wait in Arrivals with a name sign and hand the car over in the short-stay parking.",
  },
  {
    icon: Clock,
    title: "24/7, flight tracked",
    text: "Night landings and delays are covered — send your flight number and we adjust for free.",
  },
  {
    icon: Wallet,
    title: "No deposit, no credit card",
    text: "Nothing is blocked on your card. Debit card, cash in EUR or RON, or bank transfer.",
  },
  {
    icon: ShieldCheck,
    title: "Insurance & rovinieta included",
    text: "Full RCA and CASCO cover, the road vignette and unlimited mileage in Romania.",
  },
];

function Page() {
  const otopeni = getPickupPoint("otopeni");

  return (
    <>
      <section className="bg-ink py-20 text-ink-foreground">
        <div className="container-page">
          <p className="eyebrow">Henri Coandă Airport (OTP) · Free pickup</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-extrabold leading-[1.05] sm:text-5xl">
            Car Rental at Otopeni Airport, Bucharest
          </h1>
          <p className="mt-5 max-w-2xl text-base text-ink-foreground/80">
            Land at Otopeni and drive away in minutes. We deliver your car to Henri Coandă Airport
            free of charge, day or night, with no security deposit and no credit card required —
            insurance, the rovinieta and unlimited mileage are already in the daily price.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild variant="gold" size="lg">
              <Link to="/rentals">Book an airport car</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/30 bg-transparent text-ink-foreground hover:bg-white/10 hover:text-ink-foreground"
            >
              <a href="https://wa.me/40738792499" target="_blank" rel="noreferrer">
                WhatsApp your flight number
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container-page py-20">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {perks.map((p) => (
            <div key={p.title} className="rounded-xl border border-border bg-card p-6 shadow-card">
              <p.icon className="size-6 text-gold" />
              <h2 className="mt-4 font-display text-lg font-bold">{p.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-page pb-20">
        <div className="grid items-start gap-10 lg:grid-cols-2">
          <div>
            <p className="eyebrow">Where we meet</p>
            <h2 className="mt-3 text-3xl font-bold">Pickup point at Otopeni</h2>
            <p className="mt-4 text-muted-foreground">
              Henri Coandă International Airport is 17 km north of central Bucharest, roughly 30–40
              minutes by car on the DN1. After you clear Arrivals, our driver hands over the keys and
              the paperwork on the spot — the whole handover takes about ten minutes.
            </p>
            <p className="mt-4 text-muted-foreground">
              Returning the car works the same way: leave it with us at the terminal before your
              flight, at no extra cost. Prefer the city? Pickup at our office on Bulevardul Regina
              Elisabeta 33 is free too, and delivery to any other Bucharest address costs €20.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild variant="gold">
                <Link to="/rentals">See available cars</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/car-rental-no-deposit-bucharest">How no-deposit rental works</Link>
              </Button>
            </div>
          </div>
          <LocationMap point={otopeni} height="h-72" />
        </div>
      </section>

      <section className="bg-secondary/60 py-20">
        <div className="container-page">
          <div className="max-w-xl">
            <p className="eyebrow">Popular at the airport</p>
            <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Cars we deliver to OTP</h2>
            <p className="mt-3 text-muted-foreground">
              Every model in the fleet can be delivered to Otopeni — these are the ones travellers
              pick most.
            </p>
          </div>
          <CarGrid cars={cars.slice(0, 6)} className="mt-10" />
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild variant="outline">
              <Link to="/rentals">View all 28 cars</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/ro/inchirieri-auto-otopeni">
                <MapPinned className="size-4 text-gold" /> Versiunea în română
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
